import React from 'react';
import { useTranslation } from 'react-i18next';

import { SuggestionsHeader } from '../../components/SuggestionsHeader';
import { LogoSlot } from '../../components/LogoSlot';
import { LogoUploadArea } from '../../components/LogoUploadArea';
import { UploadCloudIcon, SparkleIcon, RefreshIcon } from '../../components/icons';
import { useCustomizer } from '../../store/customizer';
import type { LogoSuggestion } from '../../lib/logos';
import {
  DesktopBody,
  Grid,
  ManualLabel,
  Specs,
  SpecLine,
  UploadError,
  ModeToggle,
  ModeToggleLabel,
  MobileBody,
  MobileHeading,
  MobileRefresh,
  MobileRefreshLabel,
  MobileList,
  MobileSuccess,
  MobileSuccessBody,
} from './logo.styles';

/** The logo specs blurb (manual default). */
const LogoSpecs = (): React.ReactElement => {
  const { t } = useTranslation();
  const specLines = [
    t('File Size: Maximum 12MB'),
    t('Format: PNG or SVG'),
    t('Minimum Resolution: 300 DPI'),
  ];
  return (
    <Specs>
      <SpecLine text="Here are the specifications for the logo:" tag="paragraph" />
      {specLines.map((line) => (
        <SpecLine key={line} text="- {{line}}" placeholders={{ line }} tag="paragraph" />
      ))}
    </Specs>
  );
};

/**
 * The grid of suggestion slots. ONE component per slot (keyed by POSITION, not by
 * URL) so a slot that's still generating becomes the same slot once its logo
 * arrives — the dot-field animation never remounts/restarts. While a batch streams
 * in we render `logosCount` slots; arrived logos fill the front, the rest are null
 * (generating).
 */
const LogoSlotsView = ({
  logos,
  count,
  selectedLogo,
  onSelect,
  compact,
}: {
  logos: LogoSuggestion[];
  count: number;
  selectedLogo: string | null;
  onSelect: (cdnUrl: string) => void;
  compact?: boolean;
}): React.ReactElement => (
  <>
    {Array.from({ length: count }, (_, i) => {
      const logo = logos[i] ?? null;
      return (
        <LogoSlot
          key={i}
          index={i}
          src={logo ? logo.cdnUrl : null}
          caption={logo?.description}
          selected={!!logo && selectedLogo === logo.cdnUrl}
          onSelect={() => logo && onSelect(logo.cdnUrl)}
          compact={compact}
        />
      );
    })}
  </>
);

/**
 * Step 3 — Logo. AI-generated suggestions (a grid that streams in over the
 * websocket, "Refresh" loads a fresh batch) or upload-your-own; the AI/manual
 * identity is a toggle, not a navigable sub-screen. Desktop & mobile.
 */
export const LogoStep = (): React.ReactElement => {
  const { t } = useTranslation();
  const {
    isMobile,
    aiLogo,
    setAiLogo,
    logos,
    loadingLogos,
    logosCount,
    selectedLogo,
    selectLogo,
    refreshLogos,
    uploadedFile,
    onSelectLogoFile,
    logoError,
  } = useCustomizer();

  // While a batch streams in, render up to `logosCount` slots (arrived logos +
  // still-generating placeholders); once settled, just the arrived logos.
  const slotCount = loadingLogos ? Math.max(logosCount, logos.length) : logos.length;

  // Map the upload-failure discriminant to localized copy at the render site (so
  // the literal strings are harvested by the i18n extractor + stay reactive).
  const errorText =
    logoError === 'unsupported'
      ? t('That file can’t be used. Upload a PNG or SVG under 12MB.')
      : logoError === 'failed'
        ? t('We couldn’t process your logo. Please try again.')
        : null;

  /* ── Mobile ────────────────────────────────────────────────────────────── */
  if (isMobile) {
    if (aiLogo) {
      return (
        <MobileBody>
          <MobileHeading text="Choose your perfect logo" tag="h1" />
          <MobileRefresh type="button" onClick={refreshLogos}>
            <RefreshIcon size={16} />
            <MobileRefreshLabel text="Refresh suggestions" tag="span" />
          </MobileRefresh>
          <MobileList>
            <LogoSlotsView logos={logos} count={slotCount} selectedLogo={selectedLogo} onSelect={selectLogo} compact />
          </MobileList>
          <ModeToggle type="button" onClick={() => setAiLogo(false)}>
            <UploadCloudIcon size={18} />
            <ModeToggleLabel text="Upload your own logo" tag="span" />
          </ModeToggle>
        </MobileBody>
      );
    }

    // Manual upload (mobile).
    return (
      <MobileBody>
        <MobileHeading text="Choose your perfect logo" tag="h1" />
        <LogoUploadArea value={selectedLogo} onSelectFile={onSelectLogoFile} variant="mobile" />
        {errorText ? <UploadError text={errorText} tag="paragraph" /> : null}
        {uploadedFile || selectedLogo ? (
          <>
            <MobileSuccess text="Logo uploaded successfully!" tag="h2" />
            <MobileSuccessBody
              text="We will use your logo on the website and on channels used to communicate with players, such as emails and notifications."
              tag="paragraph"
            />
          </>
        ) : (
          <LogoSpecs />
        )}
        <ModeToggle type="button" onClick={() => setAiLogo(true)}>
          <SparkleIcon size={18} />
          <ModeToggleLabel text="Choose from generated logos" tag="span" />
        </ModeToggle>
      </MobileBody>
    );
  }

  /* ── Desktop manual upload ─────────────────────────────────────────────── */
  if (!aiLogo) {
    return (
      <DesktopBody>
        <ManualLabel text="Upload your own logo" tag="span" />
        <LogoUploadArea value={selectedLogo} onSelectFile={onSelectLogoFile} variant="desktop" />
        {errorText ? <UploadError text={errorText} tag="paragraph" /> : null}
        {selectedLogo ? null : <LogoSpecs />}
      </DesktopBody>
    );
  }

  /* ── Desktop AI-generated ──────────────────────────────────────────────── */
  return (
    <DesktopBody>
      <SuggestionsHeader label={t('Lumio’s suggestions for your Casino')} onRefresh={refreshLogos} />
      <Grid>
        <LogoSlotsView logos={logos} count={slotCount} selectedLogo={selectedLogo} onSelect={selectLogo} />
      </Grid>
    </DesktopBody>
  );
};

export default LogoStep;